import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import { DashboardPreferencesDialog } from "@/components/DashboardPreferencesDialog";
import { SessionExpiryWarning } from "@/components/SessionExpiryWarning";
import { FileMissingPostDeployListener } from "@/components/FileMissingPostDeployListener";
import { ProtectedRoute, PublicOnlyRoute } from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminsPage from "@/pages/AdminsPage";
import AdminUserSessionsPage from "@/pages/admin/AdminUserSessionsPage";
import InvitesListPage from "@/pages/admin/InvitesListPage";
import CreateInvitePage from "@/pages/admin/CreateInvitePage";
import MockEmailsPage from "@/pages/admin/MockEmailsPage";
// Phase I — Email send log (super-admin only).
import AdminEmailLogPage from "@/pages/admin/AdminEmailLogPage";
import AdminSubmissionsListPage from "@/pages/admin/AdminSubmissionsListPage";
import AdminSubmissionDetailPage from "@/pages/admin/AdminSubmissionDetailPage";
import TemplatesListPage from "@/pages/admin/TemplatesListPage";
import NewTemplatePage from "@/pages/admin/NewTemplatePage";
import TemplateDetailPage from "@/pages/admin/TemplateDetailPage";
import AdminCustomersListPage from "@/pages/admin/AdminCustomersListPage";
import AdminCustomerNewPage from "@/pages/admin/AdminCustomerNewPage";
import AdminCustomerDetailPage from "@/pages/admin/AdminCustomerDetailPage";
import AdminContractDetailPage from "@/pages/admin/AdminContractDetailPage";
import AdminContractsListPage from "@/pages/admin/AdminContractsListPage";
import CoiTemplatesPage from "@/pages/admin/CoiTemplatesPage";
import AdminRentalsListPage from "@/pages/admin/AdminRentalsListPage";
import AdminRentalDetailPage from "@/pages/admin/AdminRentalDetailPage";
import AdminBillingBackfillPage from "@/pages/admin/AdminBillingBackfillPage";
import AdminRenewalsListPage from "@/pages/admin/AdminRenewalsListPage";
import AdminRenewalsAnalyticsPage from "@/pages/admin/AdminRenewalsAnalyticsPage";
import AdminSystemSettingsPage from "@/pages/admin/AdminSystemSettingsPage";
import AdminPermissionsPage from "@/pages/admin/AdminPermissionsPage";
import TemplateGeneratorAuditPage from "@/pages/admin/TemplateGeneratorAuditPage";
// Phase H.13b — Clause Library page.
import ClausesPage from "@/pages/admin/ClausesPage";
import ImpersonationAuditPage from "@/pages/admin/ImpersonationAuditPage";
// Phase SIG-VALIDATE — Signature Audit (super_admin only).
import AdminSignatureAuditPage from "@/pages/admin/AdminSignatureAuditPage";
// Phase J.1 — Invoices.
import AdminInvoicesListPage from "@/pages/admin/AdminInvoicesListPage";
// Phase K.1 — Consolidated Billing
import AdminStatementsListPage from "@/pages/admin/AdminStatementsListPage";
import AdminStatementDetailPage from "@/pages/admin/AdminStatementDetailPage";
import AdminInvoiceDetailPage from "@/pages/admin/AdminInvoiceDetailPage";
import AdminPaymentsListPage from "@/pages/admin/AdminPaymentsListPage";
// Phase TOLLBILL-P3a (2026-07-10) — Admin Tolls surface.
import AdminTollsListPage from "@/pages/admin/AdminTollsListPage";
import AdminTollsSettingsPage from "@/pages/admin/AdminTollsSettingsPage";
// Phase TOLLBILL-P5c (2026-07-10) — Bills-first primary landing.
import AdminTollsBillsListPage from "@/pages/admin/AdminTollsBillsListPage";
// Phase J.1 Pass 3 — customer-facing invoice pages.
import CustomerInvoicesListPage from "@/pages/customer/CustomerInvoicesListPage";
import CustomerInvoiceDetailPage from "@/pages/customer/CustomerInvoiceDetailPage";
import CustomerStatementsListPage from "@/pages/customer/CustomerStatementsListPage";
import CustomerStatementDetailPage from "@/pages/customer/CustomerStatementDetailPage";
// Phase K.5 (2026-08-07) — public/tokenized statement + invoice views.
import PublicStatementView from "@/pages/public/PublicStatementView";
import PublicInvoiceView from "@/pages/public/PublicInvoiceView";
import SignContractPage from "@/pages/customer/SignContractPage";
import CoiUploadPage from "@/pages/customer/CoiUploadPage";
import CustomerDashboardPage from "@/pages/CustomerDashboardPage";
import CustomerProfilePage from "@/pages/CustomerProfilePage";
import InviteLandingPage from "@/pages/customer/InviteLandingPage";
import ClaimInvitePage from "@/pages/ClaimInvitePage";  // Phase IM — admin/manager magic-link claim
import SubmissionFormPage from "@/pages/customer/SubmissionFormPage";
// Phase J.3.2 — notifications pages (shared component, role prop).
import NotificationsPage from "@/pages/NotificationsPage";
import NotificationPreferencesPage from "@/pages/NotificationPreferencesPage";
// Phase J.3.3 — recurring billing schedules admin page.
import AdminBillingSchedulesPage from "@/pages/admin/AdminBillingSchedulesPage";
// Phase J.3.4 — payment methods (customer) + auto-charge attempts (admin).
import CustomerPaymentMethodsPage from "@/pages/customer/CustomerPaymentMethodsPage";
import CustomerActivateAchPage from "@/pages/customer/CustomerActivateAchPage";
import AdminAutoChargeAttemptsPage from "@/pages/admin/AdminAutoChargeAttemptsPage";
// Phase MGR1 — manager mobile flow.
import ManagerLandingPage from "@/pages/manager/ManagerLandingPage";
import ManagerRentalDetailPage from "@/pages/manager/ManagerRentalDetailPage";
import "@/App.css";

function RootRedirect() {
    return <Navigate to="/login" replace />;
}

function App() {
    return (
        <TenantProvider>
            <AuthProvider>
                <BrowserRouter>
                <Toaster richColors position="top-right" />
                <FileMissingPostDeployListener />
                <Routes>
                    <Route path="/" element={<RootRedirect />} />

                    <Route
                        path="/login"
                        element={
                            <PublicOnlyRoute>
                                <LoginPage />
                            </PublicOnlyRoute>
                        }
                    />
                    <Route
                        path="/signup"
                        element={
                            <PublicOnlyRoute>
                                <SignupPage />
                            </PublicOnlyRoute>
                        }
                    />
                    <Route
                        path="/forgot-password"
                        element={
                            <PublicOnlyRoute>
                                <ForgotPasswordPage />
                            </PublicOnlyRoute>
                        }
                    />
                    <Route
                        path="/reset-password"
                        element={
                            <PublicOnlyRoute>
                                <ResetPasswordPage />
                            </PublicOnlyRoute>
                        }
                    />

                    <Route
                        path="/change-password"
                        element={
                            <ProtectedRoute>
                                <ChangePasswordPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminDashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/admins"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <AdminsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/users/:id/sessions"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <AdminUserSessionsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/invites"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <InvitesListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/invites/new"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <CreateInvitePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/mock-emails"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <MockEmailsPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase I — Real email send log (super_admin only). */}
                    <Route
                        path="/admin/email-log"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <AdminEmailLogPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/submissions"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminSubmissionsListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/submissions/:id"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminSubmissionDetailPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/templates/contracts"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <TemplatesListPage templateType="rental_agreement" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/templates/ach"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <TemplatesListPage templateType="ach_authorization" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/templates/contracts/new"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <NewTemplatePage templateType="rental_agreement" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/templates/ach/new"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <NewTemplatePage templateType="ach_authorization" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/templates/invoices"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <TemplatesListPage templateType="invoice" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/templates/invoices/new"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <NewTemplatePage templateType="invoice" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/templates/:id"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <TemplateDetailPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/customers"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminCustomersListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/customers/new"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminCustomerNewPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/customers/:id"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminCustomerDetailPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/contracts/:id"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminContractDetailPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/contracts"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminContractsListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/coi-templates"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <CoiTemplatesPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/rentals"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminRentalsListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/rentals/renewals"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminRenewalsListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/renewals/analytics"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminRenewalsAnalyticsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/rentals/:id"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminRentalDetailPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase B1 follow-up #3 — bulk backfill (super_admin only). */}
                    <Route
                        path="/admin/billing/backfill"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <AdminBillingBackfillPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/settings"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <AdminSystemSettingsPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Task 13 — read-only permissions matrix (any admin/manager). */}
                    <Route
                        path="/admin/permissions"
                        element={
                            <ProtectedRoute roles={["admin", "manager", "super_admin"]}>
                                <AdminPermissionsPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase N follow-up — AI generator audit log (super_admin only). */}
                    <Route
                        path="/admin/settings/audit/template-generator"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <TemplateGeneratorAuditPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase H.13b — Clause library. super_admin AND admin can land here;
                        the page itself hides Create/Edit/Archive for non-super-admins. */}
                    <Route
                        path="/admin/clauses"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <ClausesPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/impersonation-audit"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <ImpersonationAuditPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase SIG-VALIDATE — Signature audit + retroactive scan. */}
                    <Route
                        path="/admin/diagnostics/signature-audit"
                        element={
                            <ProtectedRoute roles={["super_admin"]}>
                                <AdminSignatureAuditPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase J.1 — Invoices */}
                    <Route
                        path="/admin/invoices"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminInvoicesListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/invoices/:id"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminInvoiceDetailPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase K.1 — Consolidated Billing (statements) */}
                    <Route
                        path="/admin/statements"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminStatementsListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/statements/:id"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminStatementDetailPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase J.2 Pass 3 — Admin payments list */}
                    <Route
                        path="/admin/payments"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminPaymentsListPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase TOLLBILL-P3a — Admin Tolls */}
                    <Route
                        path="/admin/tolls"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminTollsListPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase TOLLBILL-P5c — Bills-first primary landing */}
                    <Route
                        path="/admin/tolls/bills"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminTollsBillsListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/tolls/settings"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminTollsSettingsPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase J.1 Pass 3 — Customer invoices */}
                    <Route
                        path="/customer/invoices"
                        element={
                            <ProtectedRoute roles={["customer"]}>
                                <CustomerInvoicesListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/customer/invoices/:id"
                        element={
                            <ProtectedRoute roles={["customer"]}>
                                <CustomerInvoiceDetailPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase K.3 (2026-08-06) — Customer statements portal */}
                    <Route
                        path="/customer/statements"
                        element={
                            <ProtectedRoute roles={["customer"]}>
                                <CustomerStatementsListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/customer/statements/:id"
                        element={
                            <ProtectedRoute roles={["customer"]}>
                                <CustomerStatementDetailPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route path="/sign/contract/:token" element={<SignContractPage />} />
                    <Route path="/upload-coi/:token" element={<CoiUploadPage />} />

                    {/* Phase K.5 (2026-08-07) — tokenized public billing views (no auth) */}
                    <Route path="/public/statements/:token" element={<PublicStatementView />} />
                    <Route path="/public/invoices/:token" element={<PublicInvoiceView />} />

                    {/* Phase J.4a — public ACH activation magic-link landing page */}
                    <Route path="/customer/activate-ach" element={<CustomerActivateAchPage />} />

                    <Route path="/invite/:token" element={<InviteLandingPage />} />
                    <Route path="/claim-invite/:token" element={<ClaimInvitePage />} />

                    {/* Phase J.3.2 — Notifications (customer + admin) */}
                    <Route
                        path="/customer/notifications"
                        element={
                            <ProtectedRoute roles={["customer"]}>
                                <NotificationsPage role="customer" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/customer/notifications/preferences"
                        element={
                            <ProtectedRoute roles={["customer"]}>
                                <NotificationPreferencesPage role="customer" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/notifications"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <NotificationsPage role="admin" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/notifications/preferences"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <NotificationPreferencesPage role="admin" />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase J.3.3 — Recurring billing schedules */}
                    <Route
                        path="/admin/billing-schedules"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminBillingSchedulesPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase J.3.4 — Auto-charge attempts (admin) */}
                    <Route
                        path="/admin/auto-charge-attempts"
                        element={
                            <ProtectedRoute roles={["admin", "super_admin"]}>
                                <AdminAutoChargeAttemptsPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase J.3.4 — Payment methods (customer) */}
                    <Route
                        path="/customer/payment-methods"
                        element={
                            <ProtectedRoute roles={["customer"]}>
                                <CustomerPaymentMethodsPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/customer"
                        element={
                            <ProtectedRoute roles={["customer"]}>
                                <CustomerDashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/customer/profile"
                        element={
                            <ProtectedRoute roles={["customer"]}>
                                <CustomerProfilePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/customer/submissions/:id"
                        element={
                            <ProtectedRoute roles={["customer"]}>
                                <SubmissionFormPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase MGR1 — manager mobile-only routes */}
                    <Route
                        path="/manager"
                        element={
                            <ProtectedRoute roles={["manager"]}>
                                <ManagerLandingPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/manager/rentals/:id"
                        element={
                            <ProtectedRoute roles={["manager"]}>
                                <ManagerRentalDetailPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
                <DashboardPreferencesDialog />
                <SessionExpiryWarning />
            </BrowserRouter>
            </AuthProvider>
        </TenantProvider>
    );
}

export default App;
